import { useEffect, useMemo, useState } from 'react'
import { parseISO, format, startOfMonth, endOfMonth, subMonths, addDays } from 'date-fns'
import { parseCompetenceDate } from '@utils/dates/parseCompetenceDate'

import { AddEditOtherServicesModal } from '@pages/Invoicing/components/AddEditOtherServicesModal'
import { AddEditServiceModal } from './components/AddEditServiceModal'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { BudgetPreviewsClient } from './components/BudgetPreviewsClient'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { DatePicker } from '@components/shared/DatePicker'
import { DistributeBillingModal } from './components/DistributeBillingModal'
import { emotionTheme } from '@styles/theme'
import { formattedDatePtBr } from '@utils/dates/formattedDatePtBr'
import { formattedDatePtBrToIso } from '@utils/dates/formattedDatePtBrToIso'
import { getClientDefaultPreviewNumber } from './helpers/clientServiceActions'
import { getModalPermissions } from './helpers/getModalPermissions'
import { getTabsData } from './helpers/getTabsData'
import { mapSubmitFormValues } from './helpers/mapSubmitFormValues'
import { getCompetencePeriodFromSource } from '@pages/Invoicing/components/AddEditOtherServicesModal/helpers/getCompetencePeriodFromSource'
import { mapSubmitOtherServicesFormValues } from '@pages/Invoicing/components/AddEditOtherServicesModal/helpers/mapSubmitOtherServicesFormValues'
import { parseOtherServicesGrid } from '@pages/Invoicing/helpers/parseOtherServicesGrid'
import { parseOtherServicesTotalsGrid } from '@pages/Invoicing/helpers/parseOtherServicesTotalsGrid'
import { parseWeeklyBreakdownClientGrid } from './helpers/parseWeeklyBreakdownClientGrid'
import { parseWeeklyBreakdownClientTotalGrid } from './helpers/parseWeeklyBreakdownClientTotalGrid'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnIcon } from '@assets/svgs/ReturnIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { Separator } from '@components/shared/Separator'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Tabs } from '@components/shared/Tabs'
import { Title } from '@components/shared/Title'
import { Tooltip } from '@components/shared/Tooltip'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { useClientModal } from './context'
import { useInvoicing } from '@pages/Invoicing/context'
import { useMediaQuery } from '@mui/material'
import { useUser } from '@hooks/useUser'
import { PageIds } from '@hooks/useLayout/types'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'
import { PreviewsStatus, type PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'
import { getPagePermissions } from '@hooks/useUser/provider'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'

import type { AddEditOtherServicesModalFormProps } from '@pages/Invoicing/components/AddEditOtherServicesModal/form/types'
import type { AddEditServiceModalFormProps } from './components/AddEditServiceModal/form/types'
import type { ClientModalProps } from './types'
import type { OtherServicesItemProps, WeeklyBreakdownProps } from './context/types'
import type { PaginationParams } from '@components/shared/Table/components/Pagination/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

import * as S from './styles'

export const InvoicingClientModal = ({ canEdit, open, data, previewStatus, onClose, ...rest }: ClientModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData, userPermissionsData } = useUser()
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU

  const otherServicesCompetencePeriod = useMemo(
    () => getCompetencePeriodFromSource(data as Record<string, unknown> | null, filiData),
    [data, filiData]
  )
  const { isClientsLoading, previewDetailsData } = useInvoicing()
  const {
    weeklyBreakdownData,
    isWeeklyBreakdownLoading,
    isResetClientLoading,
    isDistributeBillingLoading,
    isRemoveServiceLoading,
    getWeeklyBreakdown,
    setWeeklyBreakdownData,
    distributeBilling,
    resetClient,
    createNewService,
    editService,
    removeService,
    removeSelectedServices,
    isRemoveSelectedServicesLoading,
    getOtherServices,
    otherServicesData,
    isOtherServicesLoading,
    createNewOtherService,
    editOtherService,
    removeOtherService,
    isRemoveOtherServiceLoading
  } = useClientModal()

  const [openServiceModal, setOpenServiceModal] = useState(false)
  const [openDeleteServiceModal, setOpenDeleteServiceModal] = useState(false)
  const [deleteServiceModalData, setDeleteServiceModalData] = useState<WeeklyBreakdownProps | null>(null)
  const [serviceModalData, setServiceModalData] = useState<WeeklyBreakdownProps | null>(null)
  const [openOtherServicesModal, setOpenOtherServicesModal] = useState(false)
  const [otherServicesModalData, setOtherServicesModalData] = useState<OtherServicesItemProps | null>(null)
  const [openDeleteOtherServicesModal, setOpenDeleteOtherServicesModal] = useState(false)
  const [openDistributeBillingModal, setOpenDistributeBillingModal] = useState(false)
  const [openRestorePreviousPeriodModal, setOpenRestorePreviousPeriodModal] = useState(false)
  const [openDeleteSelectedModal, setOpenDeleteSelectedModal] = useState(false)
  const [selectedWeeklyIndices, setSelectedWeeklyIndices] = useState<number[]>([])
  const [selectionResetCounter, setSelectionResetCounter] = useState(0)
  const [weekSelected, setWeekSelected] = useState<number | null>(null)
  const [pagePermissions, setPagePermissions] = useState<PermissionsItemProps | null>(null)
  const [billingDate, setBillingDate] = useState(data?.fatDataInicio || '')

  // const hasWeekCompleted = useMemo(() => {
  //   return !!previewDetailsData?.previews?.find((item) => item.previewStatus === PreviewsStatus.COMPLETED)
  // }, [previewDetailsData?.previews])

  const hasItems = !!weeklyBreakdownData?.items?.length

  const today = new Date()
  const competenceDate = parseCompetenceDate(filiData?.startPeriod) ?? today
  const minBillingDate = addDays(startOfMonth(subMonths(competenceDate, 1)), 1)
  const maxBillingDate = endOfMonth(competenceDate)
  const hasSelectedAllWeeklyItems = hasItems && selectedWeeklyIndices.length === weeklyBreakdownData?.items.length
  const deleteSelectedServicesTitle = hasSelectedAllWeeklyItems
    ? 'Deseja realmente excluir todos os serviço(s) da página selecionada?'
    : `Deseja realmente excluir ${selectedWeeklyIndices.length} serviço(s) selecionado(s)?`

  const defaultPreviewNumber = useMemo(() => {
    return getClientDefaultPreviewNumber({
      currentPreviewNumber: previewDetailsData?.currentPreviewNumber,
      previewStatus
    })
  }, [previewDetailsData?.currentPreviewNumber, previewStatus])

  const selectedPreviewNumber = weekSelected || defaultPreviewNumber

  const currentPreviewStatus = useMemo(() => {
    return previewStatus.find((p) => p.isCurrentPreview)?.previewStatus.code
  }, [previewStatus])

  const isFirstWeekNotStarted = useMemo(() => {
    return previewStatus.some(
      (p) => p.previewNumber === 1 && p.previewStatus.code === PreviewsStatus.NOT_STARTED
    )
  }, [previewStatus])

  const isFirstWeekReturnedGu = useMemo(() => {
    return previewStatus.some(
      (p) => p.previewNumber === 1 && p.previewStatus.code === PreviewsStatus.RETURNED_GU
    )
  }, [previewStatus])

  const isFirstWeekInProgress = useMemo(() => {
    return previewStatus.some(
      (p) => p.previewNumber === 1 && p.previewStatus.code === PreviewsStatus.IN_PROGRESS
    )
  }, [previewStatus])

  const permissions = getModalPermissions({
    isGuProfile: true,
    canEditPagePermission: pagePermissions?.canEdit === true,
    canEditFromParent: canEdit,
    currentPreviewStatus,
    isFirstWeekNotStarted,
    isFirstWeekReturnedGu,
    isFirstWeekInProgress,
    apiCanEdit: weeklyBreakdownData?.canEdit,
    hasCompleteDistribution: !!data?.hasCompleteDistribution,
    items: weeklyBreakdownData?.items,
    canRestoreForecast: weeklyBreakdownData?.canRestoreForecast
  })

  const weekDaysData = useMemo(() => {
    const fromExisting = weeklyBreakdownData?.items.find(
      (item) => item.codFatAgrupado === serviceModalData?.codFatAgrupado
    )?.valorDias
    if (fromExisting?.length) return fromExisting

    const fromFirst = weeklyBreakdownData?.items[0]?.valorDias
    if (fromFirst?.length) return fromFirst

    const hasExistingItems = !!weeklyBreakdownData?.items?.length
    const billingStart = !hasExistingItems ? billingDate || data?.fatDataInicio || null : null
    const rawStartPeriod = billingStart || filiData?.startPeriod
    const endPeriod = data?.fatPeriodoFim
    if (!rawStartPeriod) return []

    const startDate = parseCompetenceDate(rawStartPeriod) ?? parseCompetenceDate(filiData?.startPeriod)
    if (!startDate) return []

    const parsedEndDate = parseCompetenceDate(endPeriod)
    const fallbackEndDate = addDays(startDate, selectedPreviewNumber * 7 - 1)
    const endDate = parsedEndDate && parsedEndDate >= startDate ? parsedEndDate : fallbackEndDate
    const dayNamesPtBr = [
      'domingo',
      'segunda-feira',
      'terça-feira',
      'quarta-feira',
      'quinta-feira',
      'sexta-feira',
      'sábado'
    ]

    const days = []
    const current = new Date(startDate)
    while (current <= endDate) {
      days.push({
        data: format(current, 'yyyy-MM-dd'),
        dia: dayNamesPtBr[current.getDay()],
        valor: 0
      })
      current.setDate(current.getDate() + 1)
    }
    return days
  }, [weeklyBreakdownData, serviceModalData, filiData, data, billingDate, selectedPreviewNumber])

  const handleGetOtherServices = (params: PaginationParams | null) => {
    getOtherServices({
      competenceDate: filiData?.startPeriod || '',
      filiCode: filiData?.filiCode || '',
      clientCode: data?.cdClienteNl || '',
      currentPage: params?.page || 1,
      itemsPerPage: params?.itemsPerPage || otherServicesData?.itemsPerPage || 10
    })
  }

  const handleAddEditOtherServices = (formData: AddEditOtherServicesModalFormProps) => {
    if (!permissions.canEditOverall) return

    const mappedFormData = mapSubmitOtherServicesFormValues({
      clientData: data,
      filiData,
      formData
    })

    if (!otherServicesModalData) {
      createNewOtherService(mappedFormData).then((isSuccess) => !!isSuccess && setOpenOtherServicesModal(false))
    } else {
      mappedFormData.serviceId = otherServicesModalData?.id

      editOtherService(mappedFormData).then((isSuccess) => {
        if (isSuccess) {
          setOpenOtherServicesModal(false)
          setOtherServicesModalData(null)
        }
      })
    }
  }

  const handleOpenRemoveServiceModal = (data?: WeeklyBreakdownProps | null) => {
    setOpenDeleteServiceModal(true)
    !!data && setDeleteServiceModalData(data)
  }

  const handleOpenDeleteSelectedModal = () => {
    setOpenDeleteSelectedModal(true)
  }

  const handleDeleteSelectedServices = async () => {
    if (!permissions.canEditOverall) return

    // Exclui somente os itens selecionados na página atual — nunca a lista inteira (todas as páginas)
    const ids = selectedWeeklyIndices
      .map((i) => weeklyBreakdownData?.items[i]?.codFatAgrupado)
      .filter(Boolean) as number[]

    if (!ids.length) return

    const isSuccess = await removeSelectedServices({
      filiCode: filiData?.filiCode!,
      clientCode: data?.cdClienteNl!,
      fatDtCompetencia: filiData?.startPeriod || '',
      deleteAll: false,
      ids
    })
    if (isSuccess) {
      setOpenDeleteSelectedModal(false)
      setSelectedWeeklyIndices([])
      setSelectionResetCounter((prevState) => prevState + 1)
    }
  }

  const handleOpenServiceModal = (data?: WeeklyBreakdownProps | null) => {
    setOpenServiceModal(true)
    !!data && setServiceModalData(data)
  }

  const handleOpenOtherServicesModal = (data?: OtherServicesItemProps) => {
    setOpenOtherServicesModal(true)
    !!data && setOtherServicesModalData(data)
  }

  const handleOpenOtherServicesDeleteModal = (data?: OtherServicesItemProps) => {
    setOpenDeleteOtherServicesModal(true)
    !!data && setOtherServicesModalData(data)
  }

  const handleGetWeeklyBreakdown = (params: PaginationParams | null, overrideDate?: string) => {
    getWeeklyBreakdown({
      fatDtCompetencia: overrideDate !== undefined ? overrideDate : filiData?.startPeriod || '',
      filiCodigo: filiData?.filiCode || '',
      cdClienteNl: data?.cdClienteNl || '',
      currentPage: params?.page || 1,
      itemsPerPage: params?.itemsPerPage || weeklyBreakdownData?.itemsPerPage || 10
    })
  }

  const handleAddEditService = (formData: AddEditServiceModalFormProps) => {
    if (!permissions.canEditOverall) return

    const mappedFormData = mapSubmitFormValues({
      clientData: data,
      filiData,
      formData
    })

    if (!serviceModalData) {
      createNewService(mappedFormData).then((isSuccess) => !!isSuccess && setOpenServiceModal(false))
    } else {
      mappedFormData.serviceId = serviceModalData?.codFatAgrupado
      mappedFormData.previewNumber = selectedPreviewNumber

      editService(mappedFormData).then((isSuccess) => !!isSuccess && setOpenServiceModal(false))
    }
  }

  const handleGetWeekSelected = () => {
    const editableStatuses: PreviewsStatusProps[] = [PreviewsStatus.NOT_STARTED, PreviewsStatus.IN_PROGRESS, PreviewsStatus.RETURNED_GU]
    const currentWeek =
      previewDetailsData?.currentPreviewNumber ||
      previewDetailsData?.previews?.find((item) => editableStatuses.includes(item.previewStatus.code))?.previewNumber

    !!currentWeek && setWeekSelected(currentWeek)
  }

  const handleClearState = () => {
    setWeeklyBreakdownData(null)
    setOtherServicesModalData(null)
    setSelectedWeeklyIndices([])
    setSelectionResetCounter((prevState) => prevState + 1)
    setWeekSelected(null)
    setBillingDate(data?.fatDataInicio || '')
  }

  const setInitialWeekSelected = () => {
    if (weeklyBreakdownData?.items[0]?.valorDias?.length && !weekSelected) {
      setWeekSelected(defaultPreviewNumber)
    }
  }

  useEffect(() => {
    setInitialWeekSelected()
  }, [defaultPreviewNumber, weeklyBreakdownData?.items])

  useEffect(() => {
    if (open) {
      handleGetOtherServices(null)
      handleGetWeeklyBreakdown(null)
    }
  }, [open])

  useEffect(() => {
    if (open && filiData?.startPeriod) {
      handleGetOtherServices(null)
      handleGetWeeklyBreakdown(null)
    }
  }, [filiData?.startPeriod, open])

  useEffect(() => {
    if (open) {
      handleGetWeekSelected()
      setBillingDate(data?.fatDataInicio || '')
    } else {
      handleClearState()
    }
  }, [data, open])

  useEffect(() => {
    setPagePermissions(getPagePermissions(PageIds.INVOICING))
  }, [userPermissionsData])

  const weeklyBreakdownGrid = parseWeeklyBreakdownClientGrid({
    weekSelected: selectedPreviewNumber,
    data: weeklyBreakdownData?.items || [],
    onEdit: handleOpenServiceModal,
    onDelete: handleOpenRemoveServiceModal,
    canEditServices: weeklyBreakdownData?.canEdit === true,
    canDeleteServices: weeklyBreakdownData?.canDeleteServices === true,
    actionsDisabled: !permissions.canEditWeeks
  })

  const otherServicesGrid = parseOtherServicesGrid({
    data: otherServicesData?.items || [],
    onEdit: handleOpenOtherServicesModal,
    onDelete: handleOpenOtherServicesDeleteModal,
    actionsDisabled: !permissions.canAddOtherService
  })

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
        heightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        headerIcon={
          <Breadcrumb
            breadcrumbData={[
              { label: 'Gestão Financeira' },
              { label: `Faturamento (${filiData?.filiCode} - ${filiData?.filiName}) ` },
              {
                label: `Cliente: ${Math.floor(Number(data?.cdClienteNl))} ${data?.desClienteNl} (${data?.tipoCliente})`
              }
            ]}
          />
        }
        onClose={onClose}
        {...rest}
      >
        <Title label="Faturamento" marginTop={0} />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider
              items={BudgetPreviewsClient({
                budget: previewDetailsData?.budgetAmount || 0,
                grossRevenue: data?.faturamentoBruto || 0,
                netRevenue: data?.faturamentoLiquido || 0
              })}
            />
          ) : (
            <PreviewWrapper>
              {BudgetPreviewsClient({
                budget: previewDetailsData?.budgetAmount || 0,
                grossRevenue: data?.faturamentoBruto || 0,
                netRevenue: data?.faturamentoLiquido || 0
              }).map((item) => item)}
            </PreviewWrapper>
          )}
        </BudgetPreviewsWrapper>

        <Separator />

        <Title
          label="Detalhamento Semanal"
          action={
            <BudgetPreviewsStatus
              status={
                previewStatus.find((item) => item.previewNumber === selectedPreviewNumber)?.previewStatus || {
                  code: PreviewsStatus.NOT_STARTED,
                  descriptionPtbr: 'Não iniciada'
                }
              }
            />
          }
        />

        <S.TabsBox>
          <Tabs
            selectedTab={selectedPreviewNumber}
            tabs={getTabsData(true)}
            onChange={(value) => setWeekSelected(value)}
          />
        </S.TabsBox>

        <S.StartOfBillingBox>
          <DatePicker
            hasController={false}
            name="startOfBilling"
            label="Início do faturamento"
            placeholder="DD/MM/AAAA"
            value={formattedDatePtBr(billingDate)}
            minDate={minBillingDate}
            maxDate={maxBillingDate}
            disabled={!permissions.canEditStartDate || isWeeklyBreakdownLoading}
            onChange={(dateStr) => {
              const isoDate = formattedDatePtBrToIso(dateStr)
              setBillingDate(isoDate)
            }}
          />

          <Button
            width={420}
            colorType="solidBlue"
            onClick={() => setOpenDistributeBillingModal(true)}
            disabled={!permissions.canDistribute || isWeeklyBreakdownLoading}
          >
            Distribuir faturamento
          </Button>
        </S.StartOfBillingBox>

        <Table
          headerTitle={!selectedPreviewNumber ? 'Detalhamento Semanal' : `Semana ${selectedPreviewNumber}`}
          headerPeriod={`Período Fat. : Início: ${data?.fatPeriodoInicio}| Fim: ${data?.fatPeriodoFim ? format(parseISO(data.fatPeriodoFim), 'dd') : ''} | Dia Fat. : ${data?.fatPeriodoDia}`}
          data={weeklyBreakdownGrid}
          totalData={parseWeeklyBreakdownClientTotalGrid({
            columns: weeklyBreakdownGrid[0] || [],
            data: weeklyBreakdownData,
            weekSelected: selectedPreviewNumber
          })}
          selectable={permissions.canEditOverall}
          selectionResetKey={`${selectedPreviewNumber}-${weeklyBreakdownData?.currentPage}-${selectionResetCounter}`}
          onSelectionChange={setSelectedWeeklyIndices}
          footer={{
            actionButton: {
              onClick: () => handleOpenServiceModal(),
              labelButton: 'Adicionar serviço',
              tooltip: !permissions.canAddService ? 'Não é possível adicionar serviço!' : '',
              disabled: !permissions.canAddService
            },
            secondActionButton: {
              onClick: handleOpenDeleteSelectedModal,
              labelButton: 'Excluir selecionadas',
              disabled: !selectedWeeklyIndices.length || !permissions.canEditOverall || !weeklyBreakdownData?.canRestoreForecast
            },
            pagination: {
              currentPage: weeklyBreakdownData?.currentPage,
              itemsPerPage: weeklyBreakdownData?.itemsPerPage,
              totalItems: weeklyBreakdownData?.totalItems,
              totalPages: weeklyBreakdownData?.totalPages,
              onPageChange: (page) => handleGetWeeklyBreakdown({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetWeeklyBreakdown({ itemsPerPage })
            }
          }}
          isLoading={isWeeklyBreakdownLoading}
        />

        <Title label="Outros serviços" />

        <Table
          headerTitle="Outros serviços"
          data={otherServicesGrid}
          totalData={parseOtherServicesTotalsGrid({
            columns: otherServicesGrid[0] || [],
            data: otherServicesData
          })}
          footer={{
            actionButton: {
              onClick: () => handleOpenOtherServicesModal(),
              labelButton: 'Adicionar outros serviços',
              tooltip: !permissions.hasDistributedBilling
                ? 'Distribua o faturamento antes de adicionar outros serviços!'
                : !permissions.canAddOtherService
                  ? 'Não é possível adicionar outros serviços!'
                  : '',
              disabled: !permissions.canAddOtherService
            },
            pagination: {
              currentPage: otherServicesData?.currentPage,
              itemsPerPage: otherServicesData?.itemsPerPage,
              totalItems: otherServicesData?.totalItems,
              totalPages: otherServicesData?.totalPages,
              onPageChange: (page) => handleGetOtherServices({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetOtherServices({ itemsPerPage })
            }
          }}
          isLoading={isOtherServicesLoading}
        />

        <S.Buttons>
          <Button width={isMobile ? '100%' : 165} colorType="outlineBlue" onClick={() => onClose(false)}>
            Cancelar
          </Button>

          {!!weeklyBreakdownData?.canRestoreForecast && (
            <Tooltip
              width="min-content"
              contentWidth={!isMobile ? 'fit-content' : undefined}
              singleLine
              placement="top"
              title={!permissions.hasDistributedBilling ? 'Só é possível restaurar a previsão após distribuir o faturamento!' : ''}
              style={{ width: isMobile ? '100%' : 'fit-content' }}
            >
              <span style={{ display: 'inline-block', width: isMobile ? '100%' : 'fit-content' }}>
                <Button
                  width={isMobile ? '100%' : 220}
                  colorType="solidBlue"
                  rightIcon={<ReturnIcon />}
                  disabled={isClientsLoading || isWeeklyBreakdownLoading || isOtherServicesLoading || !permissions.canEditOverall}
                  onClick={() => setOpenRestorePreviousPeriodModal(true)}
                >
                  Restaurar previsão
                </Button>
              </span>
            </Tooltip>
          )}
        </S.Buttons>
      </BaseModal2>

      <DistributeBillingModal
        open={openDistributeBillingModal}
        data={{ filiCodigo: filiData?.filiCode!, cdClienteNl: data?.cdClienteNl! }}
        onClose={() => setOpenDistributeBillingModal(false)}
        onConfirm={(formData) => {
          if (!permissions.canEditOverall) return

          distributeBilling({
            ...formData,
            cdClienteNl: data?.cdClienteNl!,
            filiCodigo: filiData?.filiCode!,
            fatDtCompetencia: filiData?.startPeriod || '',
            startBillingDate: billingDate
          }).then((isSuccess) => !!isSuccess && setOpenDistributeBillingModal(false))
        }}
        loading={isDistributeBillingLoading}
        canEdit={permissions.canEditOverall}
      />

      <AddEditServiceModal
        data={serviceModalData}
        clientData={data}
        competenceDate={filiData?.startPeriod || ''}
        open={openServiceModal}
        weekSelected={selectedPreviewNumber}
        weekDaysData={weekDaysData}
        onSubmitForm={(formData) => handleAddEditService(formData)}
        canEdit={permissions.canEditOverall}
        onClose={() => {
          setOpenServiceModal(false)
          setServiceModalData(null)
        }}
      />

      <AddEditOtherServicesModal
        clientData={data}
        data={otherServicesModalData}
        open={openOtherServicesModal}
        competenceMinDate={otherServicesCompetencePeriod.minDate}
        competenceMaxDate={otherServicesCompetencePeriod.maxDate}
        onClose={() => {
          setOpenOtherServicesModal(false)
          setOtherServicesModalData(null)
        }}
        onSubmitForm={(formData) => handleAddEditOtherServices(formData)}
        canEdit={permissions.canEditOverall}
      />

      <ReturnModal
        open={openDeleteOtherServicesModal}
        headerIcon={<TrashSimple />}
        headerTitle="Excluir Outros Serviços"
        title={`Você tem certeza que deseja excluir o serviço ${otherServicesModalData?.description}?`}
        message={'Após a exclusão, ele poderá ser adicionado novamente clicando em "Adicionar Outros Serviços"'}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteOtherServicesModal}
        isLoading={isRemoveOtherServiceLoading}
        onConfirm={() => {
          removeOtherService({
            serviceId: otherServicesModalData?.id!,
            filiCode: filiData?.filiCode!,
            clientCode: data?.cdClienteNl!,
            fatDtCompetencia: filiData?.startPeriod || ''
          }).then((isSuccess) => {
            if (!!isSuccess) {
              setOpenDeleteOtherServicesModal(false)
              setOtherServicesModalData(null)
            }
          })
        }}
      />

      <ReturnModal
        open={openRestorePreviousPeriodModal}
        headerIcon={<ReturnIcon />}
        headerTitle="Restaurar Previsão"
        title="Tem certeza de que deseja restaurar a previsão do faturamento do período anterior?"
        message="Essa ação substituirá todos os dados de previsão atuais."
        cancelButtonText="Cancelar"
        confirmButtonText="Restaurar"
        onClose={setOpenRestorePreviousPeriodModal}
        isLoading={isResetClientLoading}
        onConfirm={() => {
          if (!permissions.canEditOverall) return

          resetClient({
            filiCodigo: filiData?.filiCode!,
            cdClienteNl: data?.cdClienteNl!,
            fatDtCompetencia: filiData?.startPeriod || ''
          }).then((isSuccess) => {
            if (!!isSuccess) {
              setOpenRestorePreviousPeriodModal(false)
              setWeekSelected(null)
            }
          })
        }}
      />

      <ReturnModal
        open={openDeleteServiceModal}
        headerIcon={<TrashSimple />}
        headerTitle="Excluir Serviço"
        title="Deseja realmente excluir este serviço?"
        message={'Após a exclusão, ele poderá ser adicionado novamente clicando em "Adicionar Serviço"'}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteServiceModal}
        isLoading={isRemoveServiceLoading}
        onConfirm={() => {
          if (!permissions.canEditOverall) return

          removeService({
            serviceId: deleteServiceModalData?.codFatAgrupado!,
            filiCode: filiData?.filiCode!,
            clientCode: data?.cdClienteNl!,
            fatDtCompetencia: filiData?.startPeriod || ''
          }).then((isSuccess) => !!isSuccess && setOpenDeleteServiceModal(false))
        }}
      />

      <ReturnModal
        open={openDeleteSelectedModal}
        headerIcon={<TrashSimple />}
        headerTitle="Excluir Serviços"
        title={deleteSelectedServicesTitle}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteSelectedModal}
        isLoading={isRemoveSelectedServicesLoading}
        onConfirm={handleDeleteSelectedServices}
      />
    </>
  )
}
